import { useCallback, useMemo } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { parse, ParsedQs } from "qs";

import { Query } from "@/custom-types/Query";
import { leaflyShopStringify } from "@/utils/qs";

/**
 * Generic hook for removing specified keys from the query string
 *
 * Usage:
 * - Call `clearQueryStringKeys` with an array of keys to remove them from the query string.
 * - The URL is updated with the remaining query parameters using shallow routing.
 */

export const clearKeysFromQuery = (
  query: Query | ParsedQs,
  keysToRemove: string[],
): Query | ParsedQs =>
  Object.fromEntries(
    Object.entries(query).filter(([key]) => !keysToRemove.includes(key)),
  );

const useClearQueryStringKeys = () => {
  const { push } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queryString = searchParams?.toString() || "";

  const parsedQuery = useMemo(() => parse(queryString), [queryString]);

  const clearQueryStringKeys = useCallback(
    (keysToRemove: string[]) => {
      const newQueryString = leaflyShopStringify(
        clearKeysFromQuery(parsedQuery, keysToRemove),
      );

      push(`${pathname}${newQueryString ? `?${newQueryString}` : ""}`, {
        scroll: false,
      });
    },
    [queryString, pathname],
  );

  return clearQueryStringKeys;
};

export default useClearQueryStringKeys;
